import type { DashboardOpenVisits, DashboardVisit } from '@/routes/dashboard/useOpenVisits';
import { addDays, parseIsoDate, toIsoDate } from '@/lib/timezone';

/**
 * How the owner dashboard groups the API's flat rows (M2-17, Kyle's Q4 = A):
 * Late first, then Today, then each later day up to the last one, leaving out
 * empty days. Inside each section the rows go into one group per technician,
 * with Unassigned last.
 *
 * Every date here is one the SERVER computed on the account's clock (`day`,
 * `today`, `lastDay`). This module only compares those strings, so no browser
 * clock gets a vote, which keeps the one-clock rule.
 */

export interface TechnicianGroup {
  /** Null for the visits nobody is booked on. */
  technicianId: string | null;
  technicianName: string | null;
  visits: DashboardVisit[];
  /** Distinct jobs: a crew's two visits by one technician are still one job (the board's "jobs, not cards"). */
  jobCount: number;
}

export interface DashboardSection {
  kind: 'late' | 'day';
  /** The section's calendar date, `YYYY-MM-DD`. Null for Late, which spans days. */
  day: string | null;
  isToday: boolean;
  groups: TechnicianGroup[];
  jobCount: number;
}

function distinctJobs(visits: DashboardVisit[]): number {
  return new Set(visits.map((visit) => visit.jobId)).size;
}

/**
 * By technician, ordered by name with Unassigned last. Visits keep the API's
 * order, which is start order. A visit is grouped under ITS OWN technician,
 * never the job's lead (M2-52), so a crew job appears under each person
 * booked on it.
 */
export function groupByTechnician(visits: DashboardVisit[]): TechnicianGroup[] {
  const groups = new Map<string, TechnicianGroup>();
  for (const visit of visits) {
    const key = visit.technicianId ?? '';
    let group = groups.get(key);
    if (!group) {
      group = {
        technicianId: visit.technicianId ?? null,
        technicianName: visit.technicianName ?? null,
        visits: [],
        jobCount: 0,
      };
      groups.set(key, group);
    }
    group.visits.push(visit);
  }

  const all = [...groups.values()];
  for (const group of all) {
    group.jobCount = distinctJobs(group.visits);
  }

  return all.sort((a, b) => {
    if (a.technicianId === null || b.technicianId === null) {
      return a.technicianId === null ? (b.technicianId === null ? 0 : 1) : -1;
    }
    return (a.technicianName ?? '').localeCompare(b.technicianName ?? '');
  });
}

export function groupDashboard(data: DashboardOpenVisits): DashboardSection[] {
  const sections: DashboardSection[] = [];

  const late = data.visits.filter((visit) => visit.late);
  if (late.length > 0) {
    sections.push({
      kind: 'late',
      day: null,
      isToday: false,
      groups: groupByTechnician(late),
      jobCount: distinctJobs(late),
    });
  }

  const start = parseIsoDate(data.today);
  const last = parseIsoDate(data.lastDay);
  if (!start || !last) {
    return sections;
  }

  const lastIso = toIsoDate(last);
  for (let day = start; ; day = addDays(day, 1)) {
    const iso = toIsoDate(day);
    const onDay = data.visits.filter((visit) => !visit.late && visit.day === iso);
    if (onDay.length > 0) {
      sections.push({
        kind: 'day',
        day: iso,
        isToday: iso === data.today,
        groups: groupByTechnician(onDay),
        jobCount: distinctJobs(onDay),
      });
    }
    if (iso >= lastIso) {
      break;
    }
  }

  return sections;
}

/** The board for one day, narrowed to one technician when there is one (`Dispatch`'s `date` and `tech` parameters). */
export function boardHref(day: string, technicianId?: string | null): string {
  const params = new URLSearchParams({ date: day });
  if (technicianId) {
    params.set('tech', technicianId);
  }
  return `/dispatch?${params.toString()}`;
}
